/**
 * telegram.js — thin wrapper over the Telegram WebApp SDK.
 *
 * No @twa-dev/sdk dependency. The SDK is a wrapper over window.Telegram,
 * which Telegram injects via a script tag anyway — importing a package to
 * reach a global costs bundle for nothing.
 *
 * Everything here degrades to a no-op outside Telegram so the app can be
 * developed in a normal browser.
 */

const tg = () => (typeof window !== "undefined" ? window.Telegram?.WebApp : null);

export const isTelegram = () => Boolean(tg()?.initData);

/**
 * The signed payload. Sent to pe-tgauth for HMAC verification.
 *
 * Note this is initData, NOT initDataUnsafe. The latter is a parsed object
 * that any client can forge — it exists for reading display names, never for
 * auth. Nothing in this app passes it to the server.
 */
export const initData = () => tg()?.initData || "";

/** Display only. Never trusted for identity. */
export const unsafeUser = () => tg()?.initDataUnsafe?.user || null;

export function ready() {
  const t = tg();
  if (!t) return;
  t.ready();
  t.expand();
  // Telegram pulls down to close by default, which fights any scrollable
  // list. Disabling it is why a long bot list doesn't randomly dismiss.
  if (typeof t.disableVerticalSwipes === "function") t.disableVerticalSwipes();
}

/**
 * Native back button.
 *
 * Using Telegram's own chrome rather than rendering our own means no second
 * nav bar eating vertical space, and it behaves the way every other mini app
 * does — which is what users expect.
 */
export function setBack(handler) {
  const t = tg();
  if (!t?.BackButton) return () => {};
  if (handler) {
    t.BackButton.show();
    t.BackButton.onClick(handler);
    return () => {
      t.BackButton.offClick(handler);
      t.BackButton.hide();
    };
  }
  t.BackButton.hide();
  return () => {};
}

/** Haptics. Silent no-op where unsupported. */
export const haptic = {
  tap: () => tg()?.HapticFeedback?.impactOccurred?.("light"),
  ok: () => tg()?.HapticFeedback?.notificationOccurred?.("success"),
  err: () => tg()?.HapticFeedback?.notificationOccurred?.("error"),
};

/**
 * Telegram's theme colours.
 *
 * Blended with the PolyEdge palette rather than replaced: amber accent and
 * typography stay ours, background and text follow the user's Telegram
 * theme. A mini app that ignores themeParams reads as a website in a frame.
 */
export function themeVars() {
  const p = tg()?.themeParams || {};
  return {
    bg: p.bg_color || "#080705",
    secondaryBg: p.secondary_bg_color || "#0d0b08",
    text: p.text_color || "#e8d5b0",
    hint: p.hint_color || "#6b6155",
    link: p.link_color || "#f5a623",
  };
}

export function applyTheme() {
  // Deliberately does NOT adopt themeParams for the app surface.
  //
  // The first version blended Telegram's colours in. On a light-mode client
  // that inverted the palette — white background, black text, amber floating
  // on top — and the product stopped looking like itself. PolyEdge is dark by
  // identity, not preference.
  //
  // Only the surrounding Telegram chrome is set, so the header above the app
  // matches rather than clashing.
  const t = tg();
  if (!t) return;
  try {
    // Read the tier variables applyTier() set, so Telegram's chrome follows
    // the same tier colour the app is using.
    const cs = getComputedStyle(document.documentElement);
    const bg0 = (cs.getPropertyValue("--pe-bg0") || "#080705").trim();
    const bg1 = (cs.getPropertyValue("--pe-bg1") || "#0d0b08").trim();
    if (t.setHeaderColor) t.setHeaderColor(bg1);
    if (t.setBackgroundColor) t.setBackgroundColor(bg0);
  } catch { /* older clients ignore these */ }
}

export const openLink = (url) => {
  const t = tg();
  if (t?.openLink) t.openLink(url);
  else window.open(url, "_blank", "noopener");
};

export const closeApp = () => tg()?.close?.();

export function confirm(message) {
  const t = tg();
  if (!t?.showConfirm) return Promise.resolve(window.confirm(message));
  return new Promise((res) => t.showConfirm(message, res));
}
